<template>
  <div class="autoregistro-wrapper">
    <!-- ─── Título ──────────────────────────────────────────────────────────── -->
    <h5 class="autoregistro-title poppins-semi-bold-mine-shaft-20px">
      Registro como Institución
    </h5>

    <b-form @submit.prevent="enviarRegistro" novalidate>

      <!-- ── DATOS PERSONALES ─────────────────────────────────────────────── -->
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Correo electrónico *" label-class="campo-label">
            <b-form-input
              v-model="form.nombre_usuario"
              type="email"
              placeholder="ejemplo@correo.com"
              :state="validarCampo('nombre_usuario')"
              trim
            />
            <b-form-invalid-feedback>Ingrese un correo electrónico válido.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="6">
          <b-form-group label="Celular *" label-class="campo-label">
            <b-form-input
              v-model="form.celular"
              type="text"
              placeholder="3001234567"
              :state="validarCampo('celular')"
              maxlength="15"
              trim
            />
            <b-form-invalid-feedback>El celular es obligatorio y debe tener máximo 15 caracteres.</b-form-invalid-feedback>
            <small class="form-text text-muted">Máximo 15 caracteres ({{ form.celular.length }}/15)</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="4">
          <b-form-group label="Tipo de documento *" label-class="campo-label">
            <b-form-select
              v-model="form.tipo_id"
              :options="tiposId"
              :state="validarCampo('tipo_id')"
            >
              <template #first>
                <b-form-select-option value="" disabled>Seleccione...</b-form-select-option>
              </template>
            </b-form-select>
            <b-form-invalid-feedback>Seleccione el tipo de documento.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>

        <b-col cols="12" md="8">
          <b-form-group label="Número de documento *" label-class="campo-label">
            <b-form-input
              v-model="form.numero_id"
              type="text"
              placeholder="Número de identificación"
              :state="validarCampo('numero_id')"
              trim
            />
            <b-form-invalid-feedback>El número de documento es obligatorio.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Primer nombre *" label-class="campo-label">
            <b-form-input
              v-model="form.primer_nombre"
              :state="validarCampo('primer_nombre')"
              placeholder="Primer nombre"
              trim
            />
            <b-form-invalid-feedback>El primer nombre es obligatorio.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Segundo nombre" label-class="campo-label">
            <b-form-input v-model="form.segundo_nombre" placeholder="Segundo nombre (opcional)" trim />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Primer apellido *" label-class="campo-label">
            <b-form-input
              v-model="form.primer_apellido"
              :state="validarCampo('primer_apellido')"
              placeholder="Primer apellido"
              trim
            />
            <b-form-invalid-feedback>El primer apellido es obligatorio.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Segundo apellido" label-class="campo-label">
            <b-form-input v-model="form.segundo_apellido" placeholder="Segundo apellido (opcional)" trim />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- ── CONTRASEÑA ────────────────────────────────────────────────────── -->
      <b-row>
        <b-col cols="12" md="6">
          <b-form-group label="Contraseña *" label-class="campo-label">
            <b-input-group>
              <b-form-input
                v-model="form.password"
                :type="mostrarPass ? 'text' : 'password'"
                placeholder="Contraseña segura"
                :state="validarCampo('password')"
                trim
              />
              <b-input-group-append>
                <b-input-group-text class="ojo-pass" @click="mostrarPass = !mostrarPass">
                  <b-icon :icon="mostrarPass ? 'eye-slash' : 'eye'" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <!-- Indicador fuerza contraseña -->
            <div v-if="form.password" class="pass-strength mt-1">
              <div
                class="pass-bar"
                :class="fuerzaPasswordClass"
                :style="{ width: fuerzaPasswordPct + '%' }"
              ></div>
            </div>
            <small v-if="form.password" :class="fuerzaPasswordClass + '-text'" class="pass-hint">
              {{ fuerzaPasswordLabel }}
            </small>
            <b-form-invalid-feedback>
              Mínimo 8 caracteres con mayúscula, minúscula, número y carácter especial.
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="12" md="6">
          <b-form-group label="Confirmar contraseña *" label-class="campo-label">
            <b-input-group>
              <b-form-input
                v-model="form.password_confirm"
                :type="mostrarPassConfirm ? 'text' : 'password'"
                placeholder="Repite la contraseña"
                :state="validarConfirmPass"
                trim
              />
              <b-input-group-append>
                <b-input-group-text class="ojo-pass" @click="mostrarPassConfirm = !mostrarPassConfirm">
                  <b-icon :icon="mostrarPassConfirm ? 'eye-slash' : 'eye'" />
                </b-input-group-text>
              </b-input-group-append>
            </b-input-group>
            <b-form-invalid-feedback>Las contraseñas no coinciden.</b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- ── INSTITUCIÓN ───────────────────────────────────────────────────── -->
      <b-row>
        <b-col cols="12">
          <b-form-group label-class="campo-label">
            <template #label>
              Institución *
              <small class="text-muted ml-2">Busque por nombre o NIT</small>
            </template>

            <!-- Campo de búsqueda -->
            <b-input-group>
              <b-form-input
                v-model="busquedaInstitucion"
                placeholder="Escriba nombre o NIT de la institución..."
                @input="onBuscarInstitucion"
                @focus="dropdownVisible = true"
                autocomplete="off"
                :state="submitted && !form.id_cliente ? false : null"
              />
            </b-input-group>

            <!-- Dropdown de resultados -->
            <div v-if="dropdownVisible && institucionesResultados.length > 0" class="inst-dropdown">
              <div
                v-for="inst in institucionesResultados"
                :key="inst.id"
                class="inst-option"
                @mousedown.prevent="seleccionarInstitucion(inst)"
              >
                <strong>{{ inst.nombre_cliente }}</strong>
                <small class="text-muted ml-2">{{ inst.tipo_de_identificacion }}: {{ inst.numero_de_identificacion }}</small>
              </div>
            </div>

            <!-- Institución seleccionada -->
            <div v-if="form.id_cliente" class="inst-seleccionada mt-2">
              <b-badge variant="primary" class="inst-badge">
                {{ institucionSeleccionada.nombre_cliente }} — {{ institucionSeleccionada.numero_de_identificacion }}
                <b-icon icon="x-circle-fill" class="ml-1 cursor-pointer" @click="limpiarInstitucion" />
              </b-badge>
            </div>

            <!-- Crear nueva institución -->
            <div class="mt-2">
              <span class="text-muted small">¿No encuentra la institución? </span>
              <b-link class="crear-inst-link" @click="abrirModalInstitucion">
                Crear nueva institución
              </b-link>
            </div>

            <b-form-invalid-feedback v-if="submitted && !form.id_cliente" :force-show="true">
              Debe seleccionar o crear una institución.
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- ── BOTONES ───────────────────────────────────────────────────────── -->
      <div class="mt-3">
        <b-button type="submit" block class="btn-registro" :disabled="cargando">
          <span v-if="!cargando">Registrarme como Institución</span>
          <b-spinner v-else small />
        </b-button>
      </div>

      <div class="mt-2 text-center">
        <b-link class="link-volver" @click="$emit('volver')">
          ← Volver al inicio de sesión
        </b-link>
      </div>

      <!-- Mensajes globales -->
      <b-alert v-if="errorMsg" show variant="danger" class="mt-3" dismissible @dismissed="errorMsg=''">
        {{ errorMsg }}
      </b-alert>
      <b-alert v-if="exitoMsg" show variant="success" class="mt-3">
        {{ exitoMsg }}
      </b-alert>
    </b-form>

    <!-- ── MODAL: CREAR NUEVA INSTITUCIÓN ───────────────────────────────────── -->
    <b-modal
      v-model="modalInstitucion"
      title="Crear nueva institución"
      centered
      ok-title="Crear institución"
      cancel-title="Cancelar"
      :ok-disabled="creandoInst"
      @ok.prevent="crearInstitucion"
      hide-header-close
    >
      <b-form>
        <b-form-group label="Tipo de identificación (NIT/CC) *" label-class="campo-label">
          <b-form-select
            v-model="nuevaInst.tipo_de_identificacion"
            :options="tiposIdInst"
            :state="nuevaInst.tipo_de_identificacion ? null : (submitedInst ? false : null)"
          >
            <template #first>
              <b-form-select-option value="" disabled>Seleccione...</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Número de identificación (NIT) *" label-class="campo-label">
          <b-form-input
            v-model="nuevaInst.numero_de_identificacion"
            placeholder="Ej: 900123456"
            :state="nuevaInst.numero_de_identificacion ? null : (submitedInst ? false : null)"
            trim
          />
        </b-form-group>

        <b-form-group label="Nombre de la institución *" label-class="campo-label">
          <b-form-input
            v-model="nuevaInst.nombre_cliente"
            placeholder="Nombre de la institución"
            :state="nuevaInst.nombre_cliente ? null : (submitedInst ? false : null)"
            trim
          />
        </b-form-group>

        <b-form-group label="Correo electrónico" label-class="campo-label">
          <b-form-input v-model="nuevaInst.correo_electronico" type="email" placeholder="correo@institución.com" trim />
        </b-form-group>

        <b-form-group label="Celular" label-class="campo-label">
          <b-form-input v-model="nuevaInst.celular" placeholder="3001234567" maxlength="15" trim />
          <small class="form-text text-muted">Máximo 15 caracteres</small>
        </b-form-group>

        <b-alert v-if="errorInst" show variant="danger" dismissible @dismissed="errorInst=''">{{ errorInst }}</b-alert>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { RepositoryFactory } from "../repositories/RepositoryFactory";

const registro = RepositoryFactory.get("registro");

// Regex para contraseña segura: mín. 8 chars, mayúscula, minúscula, número, especial
const passSegura = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;
const emailRegex  = /\w+@\w+\.+[a-z]/;

export default {
  name: "AutoRegistroInstitucion",

  data() {
    return {
      form: {
        nombre_usuario:   "",
        tipo_id:          "",
        numero_id:        "",
        primer_nombre:    "",
        segundo_nombre:   "",
        primer_apellido:  "",
        segundo_apellido: "",
        celular:          "",
        password:         "",
        password_confirm: "",
        id_cliente:       null,
      },

      // UI state
      submitted:           false,
      cargando:            false,
      mostrarPass:         false,
      mostrarPassConfirm:  false,
      errorMsg:            "",
      exitoMsg:            "",

      // Búsqueda de institución
      busquedaInstitucion:     "",
      institucionesResultados: [],
      dropdownVisible:         false,
      institucionSeleccionada: null,
      busquedaTimer:           null,

      // Modal nueva institución
      modalInstitucion: false,
      creandoInst:      false,
      submitedInst:     false,
      errorInst:        "",
      nuevaInst: {
        tipo_de_identificacion:  "",
        numero_de_identificacion: "",
        nombre_cliente:          "",
        correo_electronico:      "",
        celular:                 "",
      },

      tiposId: [
        { value: "CC",  text: "Cédula de ciudadanía (CC)" },
        { value: "CE",  text: "Cédula de extranjería (CE)" },
        { value: "NIT", text: "NIT" },
        { value: "PP",  text: "Pasaporte (PP)" },
        { value: "TI",  text: "Tarjeta de identidad (TI)" },
      ],
      tiposIdInst: [
        { value: "NIT", text: "NIT" },
        { value: "CC",  text: "Cédula de ciudadanía" },
      ],
    };
  },

  computed: {
    // Fuerza de contraseña
    fuerzaPassword() {
      const p = this.form.password;
      if (!p) return 0;
      let score = 0;
      if (p.length >= 8)                     score++;
      if (p.length >= 12)                    score++;
      if (/[A-Z]/.test(p))                   score++;
      if (/[a-z]/.test(p))                   score++;
      if (/\d/.test(p))                      score++;
      if (/[^A-Za-z\d]/.test(p))            score++;
      return score;
    },
    fuerzaPasswordPct() {
      return Math.round((this.fuerzaPassword / 6) * 100);
    },
    fuerzaPasswordClass() {
      const s = this.fuerzaPassword;
      if (s <= 2) return "debil";
      if (s <= 4) return "media";
      return "fuerte";
    },
    fuerzaPasswordLabel() {
      const c = this.fuerzaPasswordClass;
      return c === "debil" ? "Contraseña débil" : c === "media" ? "Contraseña media" : "Contraseña fuerte";
    },
    validarConfirmPass() {
      if (!this.submitted && !this.form.password_confirm) return null;
      return this.form.password === this.form.password_confirm ? null : false;
    },
  },

  methods: {
    validarCampo(campo) {
      if (!this.submitted) return null;
      const v = this.form[campo];
      if (campo === "nombre_usuario") return emailRegex.test(v) ? null : false;
      if (campo === "password")       return passSegura.test(v) ? null : false;
      if (campo === "celular")        return v && String(v).trim() !== "" && v.length <= 15 ? null : false;
      return v && String(v).trim() !== "" ? null : false;
    },

    // ── Búsqueda de institución con debounce ─────────────────────────────
    onBuscarInstitucion() {
      clearTimeout(this.busquedaTimer);
      this.busquedaTimer = setTimeout(() => {
        this.buscarInstituciones();
      }, 400);
    },

    async buscarInstituciones() {
      try {
        const result = await registro.buscarInstituciones(this.busquedaInstitucion, 1);
        this.institucionesResultados = result.data || [];
        this.dropdownVisible = this.institucionesResultados.length > 0;
      } catch {
        this.institucionesResultados = [];
      }
    },

    seleccionarInstitucion(inst) {
      this.form.id_cliente        = inst.id;
      this.institucionSeleccionada = inst;
      this.busquedaInstitucion     = inst.nombre_cliente;
      this.dropdownVisible         = false;
    },

    limpiarInstitucion() {
      this.form.id_cliente        = null;
      this.institucionSeleccionada = null;
      this.busquedaInstitucion     = "";
    },

    // ── Modal nueva institución ──────────────────────────────────────────
    abrirModalInstitucion() {
      this.submitedInst = false;
      this.errorInst    = "";
      this.nuevaInst    = { tipo_de_identificacion: "", numero_de_identificacion: "", nombre_cliente: "", correo_electronico: "", celular: "" };
      this.modalInstitucion = true;
      this.dropdownVisible  = false;
    },

    async crearInstitucion() {
      this.submitedInst = true;
      if (!this.nuevaInst.tipo_de_identificacion) {
        this.errorInst = "El tipo de identificación es obligatorio.";
        this.$emit('registroError', this.errorInst);
        return;
      }
      if (!this.nuevaInst.numero_de_identificacion) {
        this.errorInst = "El número de identificación es obligatorio.";
        this.$emit('registroError', this.errorInst);
        return;
      }
      if (!this.nuevaInst.nombre_cliente) {
        this.errorInst = "El nombre de la institución es obligatorio.";
        this.$emit('registroError', this.errorInst);
        return;
      }
      this.creandoInst = true;
      try {
        const resp = await registro.crearInstitucion({ ...this.nuevaInst, tenant: 1 });
        this.seleccionarInstitucion({
          id:                      resp.id_cliente,
          nombre_cliente:          resp.nombre_cliente,
          numero_de_identificacion: resp.numero_de_identificacion,
          tipo_de_identificacion:  this.nuevaInst.tipo_de_identificacion,
        });
        this.modalInstitucion = false;
      } catch (err) {
        let msgError = "Error creando la institución. Intente de nuevo.";
        
        if (err.response?.data?.error) {
          const errorData = err.response.data.error;
          
          // Manejar errores específicos
          if (errorData.code === 'CAMPOS_REQUERIDOS') {
            msgError = "Faltan campos obligatorios: verifique que el nombre, tipo y número de identificación estén completos.";
          } else if (errorData.message) {
            msgError = errorData.message;
          }
        } else if (err.message) {
          msgError = err.message;
        }
        
        this.errorInst = msgError;
        this.$emit('registroError', msgError);
      } finally {
        this.creandoInst = false;
      }
    },

    // ── Envio del formulario de registro ────────────────────────────────
    async enviarRegistro() {
      this.submitted = true;
      this.errorMsg  = "";
      this.exitoMsg  = "";

      // Validaciones básicas con mensajes específicos
      if (!emailRegex.test(this.form.nombre_usuario)) {
        this.errorMsg = "Por favor ingrese un correo electrónico válido.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!passSegura.test(this.form.password)) {
        this.errorMsg = "La contraseña no cumple los requisitos de seguridad: mínimo 8 caracteres con mayúscula, minúscula, número y carácter especial.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (this.form.password !== this.form.password_confirm) {
        this.errorMsg = "Las contraseñas no coinciden. Por favor verifique.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.tipo_id) {
        this.errorMsg = "Por favor seleccione el tipo de documento.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.numero_id) {
        this.errorMsg = "Por favor ingrese el número de documento.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.primer_nombre) {
        this.errorMsg = "El primer nombre es obligatorio.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.primer_apellido) {
        this.errorMsg = "El primer apellido es obligatorio.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.celular) {
        this.errorMsg = "El número de celular es obligatorio.";
        this.$emit('registroError', this.errorMsg);
        return;
      }
      if (!this.form.id_cliente) {
        this.errorMsg = "Debe seleccionar o crear una institución.";
        this.$emit('registroError', this.errorMsg);
        return;
      }

      this.cargando = true;
      try {
        await registro.registroInstitucion({
          nombre_usuario:   this.form.nombre_usuario,
          tipo_id:          this.form.tipo_id,
          numero_id:        this.form.numero_id,
          primer_nombre:    this.form.primer_nombre,
          segundo_nombre:   this.form.segundo_nombre,
          primer_apellido:  this.form.primer_apellido,
          segundo_apellido: this.form.segundo_apellido,
          celular:          this.form.celular,
          password:         this.form.password,
          id_cliente:       this.form.id_cliente,
        });

        this.exitoMsg = "¡Registro exitoso! Revisa tu correo electrónico para activar tu cuenta.";
        setTimeout(() => {
          this.$emit("registroExitoso");
        }, 2500);
      } catch (err) {
        // Manejo de errores específicos del backend
        let msg = "Error en el registro. Intente de nuevo.";
        
        if (err.response?.data?.error) {
          const errorData = err.response.data.error;
          
          // Errores específicos por código
          switch(errorData.code) {
            case 'PASS_WEAK':
              msg = "La contraseña no es lo suficientemente segura. Debe contener mínimo 8 caracteres, incluyendo mayúscula, minúscula, número y carácter especial.";
              break;
            case 'ID_CLIENTE_REQ':
              msg = "Debe seleccionar una institución para completar el registro.";
              break;
            case 'USER_EXISTS':
              msg = "El correo electrónico ya está registrado en el sistema. Por favor use otro correo o recupere su contraseña.";
              break;
            case 'CAMPOS_REQUERIDOS':
              msg = errorData.message || "Faltan campos obligatorios por completar.";
              break;
            default:
              msg = errorData.message || msg;
          }
        } else if (err.message) {
          msg = err.message;
        }
        
        this.errorMsg = msg;
        this.$emit('registroError', msg);
      } finally {
        this.cargando = false;
      }
    },

    _cerrarDropdown(e) {
      if (!this.$el.contains(e.target)) {
        this.dropdownVisible = false;
      }
    },
  },

  // Cerrar dropdown al hacer click fuera
  mounted() {
    document.addEventListener("click", this._cerrarDropdown);
  },
  beforeDestroy() {
    document.removeEventListener("click", this._cerrarDropdown);
  },
};
</script>

<style scoped>
.autoregistro-wrapper {
  max-width: 700px;
  margin: 0 auto;
  padding: 1.5rem;
  background: var(--zircon, #f4f7ff);
  border-radius: 12px;
}

.autoregistro-title {
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--mine-shaft, #333);
}

.campo-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--tuatara, #444);
}

/* ── Fuerza de contraseña ───────────────────────────── */
.pass-strength {
  height: 5px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}
.pass-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s, background-color 0.3s;
}
.pass-bar.debil  { background-color: #e74c3c; }
.pass-bar.media  { background-color: #f39c12; }
.pass-bar.fuerte { background-color: #27ae60; }
.pass-hint { font-size: 0.75rem; }
.debil-text  { color: #e74c3c; }
.media-text  { color: #f39c12; }
.fuerte-text { color: #27ae60; }

/* ── Ojo contraseña ─────────────────────────────────── */
.ojo-pass {
  cursor: pointer;
  border-top-right-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
  border: 1px solid #ced4da;
  background: #fff;
}

/* ── Dropdown instituciones ─────────────────────────── */
.inst-dropdown {
  position: absolute;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  max-height: 220px;
  overflow-y: auto;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
}
.inst-option {
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.875rem;
}
.inst-option:hover {
  background: var(--hawkes-blue, #e8eeff);
}
.inst-seleccionada { font-size: 0.875rem; }
.inst-badge {
  font-size: 0.85rem;
  padding: 0.4em 0.75em;
  cursor: default;
  background-color: var(--blue-violet, #5e35b1) !important;
}
.cursor-pointer { cursor: pointer; }
.crear-inst-link {
  color: var(--blue-violet, #5e35b1);
  font-size: 0.85rem;
  cursor: pointer;
  font-weight: 600;
}
.crear-inst-link:hover { text-decoration: underline; }

/* ── Botón principal ────────────────────────────────── */
.btn-registro {
  background-color: var(--blue-violet, #5e35b1) !important;
  border-radius: 10px !important;
  border-color: transparent !important;
  height: 48px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #fff !important;
}
.btn-registro:hover {
  background: var(--hover, #4527a0) !important;
}
.link-volver {
  color: var(--blue-violet, #5e35b1);
  font-size: 0.85rem;
  cursor: pointer;
}
.link-volver:hover { text-decoration: underline; }
</style>
